<template>
    <div class="app-content-builder-sidebar app-vertical-navigation">
        <div class="sidebar-project" v-if="isProject">
            <div class="app-vertical-navigation__header">
                <h3 class="app-vertical-navigation__header-title">Data Pages</h3>
            </div>
            <content-builder-dataset
                v-for="set in availableDatasets"
                :key="`set-${set.id}`"
                :id="set.id"
                :title="set.title"
                :name="set.name"
                :status="set.status"
                :virtual="set.virtual"
                :schema-ref="set.schema_ref">
            </content-builder-dataset>
        </div>
        <div class="sidebar-learning" v-if="isLearningObject">
            <div class="app-vertical-navigation__header">
                <h3 v-if="isTemplate" class="app-vertical-navigation__header-title">Templates</h3>
                <h3 v-else class="app-vertical-navigation__header-title">Outline</h3>
                <div class="app-vertical-navigation__header-buttons">
                    <button
                        @click="revertOrderChanges()"
                        :class="['element-button--ghost', 'element-button', hasOrderChanged ? '' : 'is--disabled']"
                        :disabled="!hasOrderChanged"
                        title="Undo Changes">
                        Undo Changes
                    </button>
                    <content-builder-create-page></content-builder-create-page>
                </div>
            </div>
            <div class="app-vertical-navigation__body">
                <div v-if="!isTemplate" class='app-vertical-navigation__menu-item is--active' disabled>
                    <svg class="element-icon--editor-details element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-editor-details"></use></svg>
                    <h4 class='app-vertical-navigation__menu-item-title'>Details</h4>
                </div>
                <div v-if="!isTemplate" class="app-vertical-navigation__submenu-item is--static">
                    <div class="input text">
                        <label>Learning Object Title</label>
                        <div v-html="source.name"
                            contenteditable="true"
                            class="input"
                            spellcheck="false"
                            @blur="onDetailsChanged($event, 'name')"
                            title="Learning Object Title">
                        </div>
                    </div>
                </div>
                <div class='app-vertical-navigation__menu-item is--active' disabled>
                    <svg class="element-icon--editor-screen element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-editor-screen"></use></svg>
                    <h4 class='app-vertical-navigation__menu-item-title'>Screens</h4>
                </div>
                <div class="app-vertical-navigation__outline">
                    <p v-if="!outline.length"
                        class="app-vertical-navigation__empty">
                        No screens have been added. Select "Add Screen" to get started!
                    </p>
                    <draggable v-model="outline"
                        group="outline"
                        class="app-vertical-navigation__outline-list">
                        <transition-group>
                            <content-builder-data-object
                                v-for="item in outline"
                                :key="`item-${item.id}`"
                                class="app-vertical-navigation__submenu-item"
                                :id="item.id"
                                :title="item.title"
                                :status="item.status"
                                :name="item.name"
                                :type="getInteractionNameForPage(item)">
                            </content-builder-data-object>
                        </transition-group>
                    </draggable>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { VueDraggableNext as Draggable } from 'vue-draggable-next'
import ContentBuilderCreatePage from './ContentBuilderCreatePage'
import ContentBuilderDataset from './ContentBuilderDataset'
import ContentBuilderDataObject from './ContentBuilderDataObject'

export default {
    components: {
        ContentBuilderCreatePage,
        ContentBuilderDataObject,
        ContentBuilderDataset,
        Draggable
    },
    computed: {
        ...mapState('builder', {
            details: state => state.details,
            selectedId: state => state.selectedId,
            client: state => state.client,
            project: state => state.project,
            source: state => state.source,
            sourceType: state => state.sourceType,
            dataObjects: state => state.dataObjects,
            datasets: state => state.datasets
        }),
        ...mapGetters('builder', [
            'dirty',
            'changes',
            'manifest',
            'getSchema',
            'hasOrderChanged',
            'getInteractionNameForPage',
            'statusMap'
        ]),
        isProject () {
            return this.sourceType === 'project'
        },
        isLearningObject () {
            return this.sourceType === 'learning'
        },
        isTemplate () {
            return this.source.is_template
        },
        availableDatasets () {
            const available = _.map(this.manifest.datasets.required, (set) => {
                const dataObject = _.find(this.dataObjects, { name: set.name })
                return {
                    ...dataObject,
                    title: set.label,
                    name: set.name,
                    schema_ref: set.$ref,
                    type: set.type || 'global',
                    status: this.statusMap[set.id] ?? false
                }
            })
            return available
        },
        outline: {
            get () {
                return _.map(this.$store.state.builder.outline, (id) => {
                    const object = _.find(this.dataObjects, { id })
                    return {
                        ...object,
                        status: this.statusMap[id] ?? false
                    }
                })
            },
            set (value) {
                const outline = _.map(value, 'id')
                this.$store.commit('builder/setOutline', outline)
            }
        }
    },
    watch: {
        outline (value) {
            _.each(value, ({ id, sort_order }, index) => {
                if (index !== sort_order) {
                    this.$store.commit('builder/updateDataObject', { id, sort_order: index })
                }
            })
        }
    },
    methods: {
        onDetailsChanged (e, prop) {
            this.$store.commit('builder/updateDetails', { [prop]: e.target.textContent || e.target.innerText })
        },
        select (id) {
            this.$store.dispatch('builder/selectObject', id)
        },
        async enableDataset (dataObject) {
            // TODO
        },
        async disableDataset (dataObject) {
            if (!confirm('Are you sure you want to remove this dataset?')) {
                return
            }

            // TODO
        },
        revertOrderChanges () {
            this.outline = _.sortBy(this.outline, (object, index) => {
                const sourceObject = _.find(this.$store.state.builder.dataObjectSource, { id: object.id })
                return sourceObject ? sourceObject.sort_order : index
            })
        }
    }
}
</script>
